import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Divider,
  IconButton,
  MenuItem,
  Pagination,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  healthCheckWarnings,
  healthCheckWarningsTruncated,
} from './auditHealthCheck';
import {
  completionPlanItems,
  completionPlanTruncated,
} from './auditCompletionPlan';
import { inviteRightItems, inviteRightsTruncated } from './auditInviteRights';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PersonIcon from '@mui/icons-material/Person';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import TabletMacIcon from '@mui/icons-material/TabletMac';
import HubIcon from '@mui/icons-material/Hub';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import {
  AuditActorType,
  AuditCategory,
  IAuditEvent,
  useAuditLog,
} from '../hooks/reactQuerys/AuditQueries';
import {
  ACTIVITY_LOG_CHIP,
  ACTIVITY_LOG_CHIP_CELL,
  ACTIVITY_LOG_ROW,
  ACTIVITY_LOG_SPINE,
} from './activityLogLayout';
import {
  actorText,
  IAuditArea,
  actorTypeLabel,
  changeText,
  dayHeading,
  entityLabel,
  areaLabel,
  areaQuery,
  eventHeadline,
  sourceLabel,
  timeText,
} from './auditText';

/**
 * Änderungsprotokoll einer Entität (#1330).
 *
 * Bewusst zurückhaltend: der Log ist nichts, was ein Mitarbeiter im normalen
 * Gebrauch braucht — er kommt hierher, wenn er eine konkrete Frage hat. Deshalb
 * zeigt die Standardansicht nur fachliche Ereignisse; generische Feld-Diffs,
 * Speichervorgänge und Portal-Übertragungen erst über den Schalter.
 *
 * Ein Ereignis kann zu mehreren Objekten gehören. Die Abfrage läuft über die
 * Kontext-Tabelle, deshalb erscheint eine im Vorgang erstellte Aufgabe hier und
 * im Aufgaben-Log — aus einem gespeicherten Eintrag.
 */

interface IProps {
  entityType: string;
  entityId: number | string;
  /** Erst laden, wenn der Reiter offen ist. */
  enabled?: boolean;
  /** Filterchips für die Bereiche dieser Entität (Kontext-Entitätsarten). */
  areas?: IAuditArea[];
  rowsPerPage?: number;
}

/**
 * Was die Ansicht zeigt.
 *
 * `domain` sind die fachlichen Ereignisse. Der Schalter „technische Änderungen
 * anzeigen" ist entfallen: dahinter standen nur drei Sorten — die geglückte
 * Portal-Übertragung, ein nacktes „gespeichert" und die beiden Hälften eines
 * Dokument-Uploads — und alle drei sagten dasselbe ein zweites Mal und
 * schlechter. Geschrieben werden sie weiterhin, für eine spätere Fehlersuche
 * in der Datenbank.
 *
 * `legacy` sind die Einträge aus den vier Alt-Tabellen, die
 * `20260914_legacy_log_migration.sql` hierher überführt hat. Sie stehen
 * gleichberechtigt zwischen den neuen — alte Vorgänge hätten sonst gar kein
 * Protokoll mehr, seit die eingeklappte Alt-Liste entfallen ist (#1330).
 */
export const ACTIVITY_LOG_CATEGORIES: AuditCategory[] = ['domain', 'legacy'];

/**
 * Soll die Herkunft als Chip an der Gruppenzeile stehen?
 *
 * Sie ist eine Ausnahmemeldung: „das kam nicht aus der Oberfläche, sondern aus
 * dem Portal / dem Nachtlauf / dem Grundbuchportal". Aus der Oberfläche ist der
 * Normalfall und bleibt deshalb stumm.
 *
 * Migrierte Alt-Einträge tragen zwangsläufig `unknown` — die vier Alt-Tabellen
 * hatten keine Spalte für den Weg. „unbekannt" an jeder dieser Zeilen sagt
 * nichts, was die Akteurszeile („Altprotokoll") nicht schon sagt.
 *
 * Ein `unknown` an einem NEUEN Ereignis bleibt dagegen sichtbar: es bedeutet,
 * dass eine Codestelle ihren Audit-Kontext nicht gesetzt hat, und genau diese
 * Lücke soll auffallen (`app/logic/audit/context.py`, `ensure`).
 */
export const showSourceChip = (event: IAuditEvent): boolean =>
  event.Category !== 'legacy' && event.Source !== 'ui';

const ACTOR_ICON: Record<AuditActorType, React.ReactElement> = {
  user: <PersonIcon fontSize='inherit' />,
  client: <HomeWorkIcon fontSize='inherit' />,
  system: <SettingsSuggestIcon fontSize='inherit' />,
  device: <TabletMacIcon fontSize='inherit' />,
  integration: <HubIcon fontSize='inherit' />,
};

/** Ereignisse eines Speichervorgangs gehören zusammen. */
interface IGroup {
  correlationId: string;
  occurredAt: string;
  lead: IAuditEvent;
  events: IAuditEvent[];
}

/** Speichern und Portal-Übertragung zu EINER Zeile zusammenfassen.
 *
 * Beides steht in derselben Klammer und gehört zusammen: "gespeichert und ins
 * Mandantenportal übertragen" bzw. — und das ist die wichtige Auskunft —
 * "gespeichert, aber nicht ins Mandantenportal übertragen". Zwei getrennte
 * Zeilen pro Speichervorgang sind Rauschen, das nichts hinzufügt.
 *
 * Die Übertragung der Einstellungen fällt dabei weg: sie läuft bei jedem
 * Speichern mit, betrifft den Vorgang aber gar nicht.
 */
export const mergeSaveAndSync = (events: IAuditEvent[]): IAuditEvent[] => {
  const gespeichert = events.find(e => e.EventType.endsWith('.saved'));
  if (!gespeichert) return events;

  const fehlgeschlagen = events.some(e => e.EventType.endsWith('.sync_failed'));
  const uebertragen = events.some(e => e.EventType === 'portal.synced');
  /* Die Warnungen sind ein eigenes Ereignis, gehoeren aber zu DIESEM
     Speichern. Ohne sie hier standen zwei Zeilen fuer einen Klick: „mit 2
     Warnungen gespeichert" und „Vorgang gespeichert und ins Mandantenportal
     uebertragen". Die zweite sagte nichts, was die erste nicht schon sagte. */
  const mitWarnungen = events.find(e =>
    e.EventType.endsWith('.saved_with_warnings')
  );
  if (!fehlgeschlagen && !uebertragen && !mitWarnungen) return events;

  const zusammengefasst: IAuditEvent = {
    ...gespeichert,
    /* Der Typ der Warnungen gewinnt -- an ihm haengt die Liste hinter „mehr".
       Die Stelle bleibt die des Speicherns: der Satz ist das Fazit der
       Gruppe und gehoert ans Ende, nicht zwischen die Einzelaenderungen. */
    EventType: mitWarnungen ? mitWarnungen.EventType : gespeichert.EventType,
    Metadata: {
      ...(gespeichert.Metadata ?? {}),
      ...(mitWarnungen?.Metadata ?? {}),
      ...(fehlgeschlagen || uebertragen
        ? { syncState: fehlgeschlagen ? 'failed' : 'ok' }
        : {}),
    },
  };
  return events
    .filter(e => !e.EventType.startsWith('portal.'))
    .filter(e => e !== mitWarnungen)
    .map(e => (e === gespeichert ? zusammengefasst : e));
};

const groupByCorrelation = (events: IAuditEvent[]): IGroup[] => {
  const order: string[] = [];
  const buckets = new Map<string, IAuditEvent[]>();
  events.forEach(event => {
    const key = event.CorrelationId || `single-${event.idAuditEvent}`;
    if (!buckets.has(key)) {
      buckets.set(key, []);
      order.push(key);
    }
    buckets.get(key)?.push(event);
  });
  return order.map(key => {
    const list = mergeSaveAndSync(
      (buckets.get(key) ?? [])
        .slice()
        .sort((a, b) => a.SequenceInCorrelation - b.SequenceInCorrelation)
    );
    return {
      correlationId: key,
      occurredAt: list[0]?.OccurredAt ?? '',
      lead: list[0],
      events: list,
    };
  });
};

/** Eine Ereigniszeile innerhalb einer Gruppe. */
const EventLine: React.FC<{
  event: IAuditEvent;
  primaryType: string;
  areas?: IAuditArea[];
}> = ({ event, primaryType, areas }) => {
  const kopf = eventHeadline(event, primaryType);
  /* Speichern trotz Health-Check-Warnungen (#1283): die Zeile nennt nur die
     Zahl, die Funde stehen eingeklappt dahinter. Ausgeschrieben waeren es bei
     einem unvollstaendigen Vorgang schnell zwanzig Zeilen im Protokoll. */
  const [detailsOffen, setDetailsOffen] = useState(false);
  /* Zwei Ereignisarten bringen eine Liste mit, die nicht in die Zeile passt:
     die Warnungen beim Speichern und der Vollzugsplan beim Anlegen. Beide
     nutzen dieselbe Aufklapp-Mechanik. */
  const details: { text: string; untergeordnet: boolean }[] = [
    ...healthCheckWarnings(event).map(w => ({
      text: w.message,
      untergeordnet: false,
    })),
    /* Massnahmen gehoeren unter ihre Phase — eingerueckt bildet die Liste ab,
       wie der Vollzug spaeter ablaeuft. Flach sahen beide gleich aus. */
    ...completionPlanItems(event).map(i => ({
      text: i.name,
      untergeordnet: i.kind === 'requirement',
    })),
    /* Die Einladungsrechte aus der Rechtsgeschaeftsart -- elf Zeilen, die
       niemand einzeln entschieden hat, stehen als eine mit Liste dahinter. */
    ...inviteRightItems(event).map(text => ({ text, untergeordnet: false })),
  ];
  const detailsGekappt =
    healthCheckWarningsTruncated(event) ||
    completionPlanTruncated(event) ||
    inviteRightsTruncated(event);
  const bereich = areaLabel(event, areas);

  // Der Chip nennt die fremde Entitätsart -- aber nur, wenn die Kopfzeile sie
  // nicht ohnehin schon nennt. Sonst stand da "Dokument" und darunter "Datei
  // hinzugefügt ... in Urkunden", also zweimal dasselbe. Bei einer Verknüpfung
  // wäre der Chip der Tabellenname der Verknüpfung selbst; das Gegenstück
  // benennt die Kopfzeile schon besser.
  const verknuepfung = !!(event.Metadata ?? {})['relation'];
  const artName = entityLabel(event.EntityType);
  const fremd =
    event.EntityType !== primaryType &&
    !verknuepfung &&
    !kopf.includes(artName);
  const fehler =
    event.EventType.endsWith('.sync_failed') ||
    (event.Metadata ?? {})['syncState'] === 'failed';

  return (
    <Box sx={ACTIVITY_LOG_ROW}>
      {/* Feste Spalte: der Bereich ist die Dimension, nach der gesucht wird --
          dieselbe, die auch im Filter oben steht. Sie sitzt deshalb immer an
          derselben Stelle, auch wenn die Zeile keinen Bereich hat. */}
      <Box sx={ACTIVITY_LOG_CHIP_CELL}>
        {fehler && (
          <WarningAmberIcon color='warning' sx={{ fontSize: '1rem' }} />
        )}
        {bereich && (
          <Chip
            size='small'
            variant='outlined'
            label={bereich}
            sx={ACTIVITY_LOG_CHIP}
          />
        )}
      </Box>

      <Box sx={{ minWidth: 0 }}>
        {/* Die fremde Entitaetsart gehoert in den Satz, nicht in die feste
            Spalte: zwei Chips nebeneinander sprengen deren Breite, und die
            Ausrichtung waere wieder dahin. */}
        {fremd && (
          <Typography
            variant='body2'
            component='span'
            color='text.secondary'
            sx={{ mr: 0.75 }}
          >
            {entityLabel(event.EntityType)}
          </Typography>
        )}
        <Typography variant='body2' component='span'>
          {kopf}
        </Typography>
        {details.length > 0 && (
          <Link
            component='button'
            type='button'
            variant='body2'
            underline='hover'
            onClick={() => setDetailsOffen(offen => !offen)}
            sx={{ ml: 1, verticalAlign: 'baseline' }}
          >
            {detailsOffen ? 'weniger' : 'mehr'}
          </Link>
        )}
        {(event.Changes ?? []).map((change, index) => (
          <Typography
            key={`${event.idAuditEvent}-${change.field}-${index}`}
            variant='body2'
            color='text.secondary'
            sx={{
              display: 'block',
              wordBreak: 'break-word',
              fontSize: '0.8125rem',
              lineHeight: 1.5,
            }}
          >
            {changeText(change)}
          </Typography>
        ))}
        <Collapse in={detailsOffen} unmountOnExit>
          <Box component='ul' sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {details.map((zeile, index) => (
              <Typography
                key={`${event.idAuditEvent}-detail-${index}`}
                component='li'
                variant='body2'
                color='text.secondary'
                sx={{
                  wordBreak: 'break-word',
                  ml: zeile.untergeordnet ? 2 : 0,
                  listStyleType: zeile.untergeordnet ? 'circle' : 'disc',
                  fontWeight: zeile.untergeordnet ? 400 : 500,
                }}
              >
                {zeile.text}
              </Typography>
            ))}
          </Box>
          {detailsGekappt && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', pl: 2.5 }}
            >
              Weitere Einträge wurden nicht mitgeschrieben.
            </Typography>
          )}
        </Collapse>
      </Box>
    </Box>
  );
};

export const ActivityLog: React.FC<IProps> = ({
  entityType,
  entityId,
  enabled = true,
  areas,
  rowsPerPage = 25,
}) => {
  const [page, setPage] = useState(1);
  const [actorType, setActorType] = useState<AuditActorType | ''>('');
  const [bereich, setBereich] = useState<string>('');
  const [offen, setOffen] = useState<Set<string>>(new Set());

  const category = ACTIVITY_LOG_CATEGORIES;

  const {
    auditEvents,
    auditCount,
    isLoadingAudit,
    isErrorAudit,
    refetchAudit,
  } = useAuditLog({
    entityType,
    entityId,
    page,
    rowsPerPage,
    category,
    actorType,
    enabled,
    ...areaQuery(bereich, areas),
  });

  /*
    Der Bereich wird serverseitig gefiltert. Vorher lief das ueber die bereits
    geladene Seite -- bei 25 Zeilen zeigte "Termine" also die Termine der
    ersten Seite und sonst nichts, und die Blaetterleiste behielt die
    Seitenzahl des ungefilterten Protokolls. Aufgeloest wird der Bereich aus
    derselben Definition, aus der auch der Chip an der Zeile kommt (#1330).
  */
  const gruppen = useMemo(() => groupByCorrelation(auditEvents), [auditEvents]);
  const seiten = Math.max(1, Math.ceil(auditCount / rowsPerPage));

  const toggle = (key: string) =>
    setOffen(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });

  if (isLoadingAudit) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={26} />
      </Box>
    );
  }

  if (isErrorAudit) {
    return (
      <Alert
        severity='warning'
        variant='outlined'
        action={
          <Typography
            variant='body2'
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={() => refetchAudit()}
          >
            Erneut versuchen
          </Typography>
        }
      >
        Das Änderungsprotokoll konnte nicht geladen werden.
      </Alert>
    );
  }

  return (
    <Stack spacing={1.5}>
      {/* Filter — bewusst schlank: wer den Log öffnet, hat eine konkrete Frage */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ sm: 'center' }}
        flexWrap='wrap'
        useFlexGap
      >
        {areas && areas.length > 0 && (
          <TextField
            select
            size='small'
            label='Bereich'
            value={bereich}
            onChange={event => {
              setBereich(event.target.value);
              setPage(1);
            }}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value=''>Alle Bereiche</MenuItem>
            {areas.map(area => (
              <MenuItem key={area.label} value={area.label}>
                {area.label}
              </MenuItem>
            ))}
          </TextField>
        )}
        <TextField
          select
          size='small'
          label='Ausgelöst von'
          value={actorType}
          onChange={event => {
            setActorType(event.target.value as AuditActorType | '');
            setPage(1);
          }}
          sx={{ minWidth: 170 }}
        >
          <MenuItem value=''>Allen</MenuItem>
          <MenuItem value='user'>Mitarbeitern</MenuItem>
          <MenuItem value='client'>Mandanten</MenuItem>
          <MenuItem value='system'>dem System</MenuItem>
          <MenuItem value='device'>Geräten</MenuItem>
          <MenuItem value='integration'>Fremdsystemen</MenuItem>
        </TextField>
        <Box sx={{ flexGrow: 1 }} />
      </Stack>

      {gruppen.length === 0 && (
        <Alert severity='info' variant='outlined'>
          Für dieses Objekt liegen keine Protokolleinträge vor.
        </Alert>
      )}

      <Stack divider={<Divider flexItem />} spacing={0}>
        {gruppen.map((gruppe, index) => {
          const tagvorher = gruppen[index - 1]?.occurredAt;
          const neuerTag =
            index === 0 ||
            dayHeading(tagvorher ?? '') !== dayHeading(gruppe.occurredAt);
          const mehrere = gruppe.events.length > 1;
          const aufgeklappt = offen.has(gruppe.correlationId) || !mehrere;

          return (
            <React.Fragment key={gruppe.correlationId}>
              {neuerTag && (
                <Typography
                  variant='overline'
                  color='text.secondary'
                  sx={{ mt: index === 0 ? 0 : 2, display: 'block' }}
                >
                  {dayHeading(gruppe.occurredAt)}
                </Typography>
              )}

              <Box sx={{ py: 0.75 }}>
                <Stack direction='row' spacing={1} alignItems='center'>
                  {mehrere && (
                    <IconButton
                      size='small'
                      onClick={() => toggle(gruppe.correlationId)}
                      aria-label={aufgeklappt ? 'Zuklappen' : 'Aufklappen'}
                    >
                      {aufgeklappt ? (
                        <ExpandLessIcon fontSize='small' />
                      ) : (
                        <ExpandMoreIcon fontSize='small' />
                      )}
                    </IconButton>
                  )}
                  {!mehrere && <Box sx={{ width: 30 }} />}

                  <Tooltip title={actorTypeLabel(gruppe.lead.ActorType)}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        fontSize: '1.1rem',
                        color: 'text.secondary',
                      }}
                    >
                      {ACTOR_ICON[gruppe.lead.ActorType] ?? (
                        <SettingsSuggestIcon fontSize='inherit' />
                      )}
                    </Box>
                  </Tooltip>

                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    {actorText(gruppe.lead)}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {timeText(gruppe.occurredAt)}
                  </Typography>
                  {showSourceChip(gruppe.lead) && (
                    <Chip
                      size='small'
                      variant='outlined'
                      label={sourceLabel(gruppe.lead.Source)}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                  {mehrere && (
                    <Typography variant='caption' color='text.secondary'>
                      · {gruppe.events.length} Änderungen
                    </Typography>
                  )}
                </Stack>

                <Collapse in={aufgeklappt} unmountOnExit>
                  <Box sx={ACTIVITY_LOG_SPINE}>
                    {gruppe.events.map(event => (
                      <EventLine
                        key={event.idAuditEvent}
                        event={event}
                        primaryType={entityType}
                        areas={areas}
                      />
                    ))}
                  </Box>
                </Collapse>
              </Box>
            </React.Fragment>
          );
        })}
      </Stack>

      {seiten > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 1 }}>
          <Pagination
            size='small'
            count={seiten}
            page={page}
            onChange={(_event, value) => setPage(value)}
          />
        </Box>
      )}
    </Stack>
  );
};
